import { FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { PasswordInput } from '../components/auth/PasswordInput';

export function ForgotPassword() {
  const [username, setUsername] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [step, setStep] = useState<'request' | 'reset'>('request');
  const [message, setMessage] = useState('');
  const [localError, setLocalError] = useState('');
  const { forgotPassword, confirmForgotPassword, loading, error, clearError } = useAuth();
  const navigate = useNavigate();

  const displayError = localError || error;

  async function handleRequest(e: FormEvent) {
    e.preventDefault();
    setLocalError('');
    clearError();
    setMessage('');

    if (!username) {
      setLocalError('Please enter your username or email');
      return;
    }

    try {
      await forgotPassword(username);
      setStep('reset');
      setMessage('Code sent — check your email or linked account.');
    } catch {
      // Error handled by context
    }
  }

  async function handleReset(e: FormEvent) {
    e.preventDefault();
    setLocalError('');
    clearError();

    if (!code || !password || !confirmPassword) {
      setLocalError('Please fill in all fields');
      return;
    }

    if (password !== confirmPassword) {
      setLocalError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setLocalError('Password must be at least 8 characters');
      return;
    }

    try {
      await confirmForgotPassword(username, code, password);
      navigate('/login');
    } catch {
      // Error handled by context
    }
  }

  return (
    <div className="auth-screen">
      <div className="auth-layout">
        <aside className="auth-story">
          <span className="story-kicker">Reset in seconds</span>
          <h1>Lost access? We’ll get you back in.</h1>
          <p>
            Request a reset code, confirm it, and set a new password without leaving this screen.
          </p>
          <ul className="benefit-list" aria-label="Reset benefits">
            <li>Instant password recovery</li>
            <li>Works for both password and federated accounts</li>
            <li>Secure custom verification codes</li>
          </ul>
        </aside>

        <section className="auth-card-wrap">
          <div className="auth-card auth-card--narrow">
            <h2 className="auth-title">{step === 'request' ? 'Forgot your password?' : 'Set a new password'}</h2>
            <p className="auth-subtitle">
              {step === 'request'
                ? 'Tell us the username on file and we’ll email instructions.'
                : 'Enter the code you received and choose a new password.'}
            </p>

            {displayError && <div className="auth-error">{displayError}</div>}
            {message && <div className="auth-success" role="status">{message}</div>}

            {step === 'request' ? (
              <form onSubmit={handleRequest} className="auth-form">
                <label htmlFor="reset-username" className="auth-label">
                  Username or email
                </label>
                <input
                  id="reset-username"
                  type="text"
                  className="auth-input"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter username or email"
                  disabled={loading}
                />

                <button type="submit" className="btn btn-solid auth-submit" disabled={loading}>
                  {loading ? 'Sending code...' : 'Send reset code'}
                </button>
              </form>
            ) : (
              <form onSubmit={handleReset} className="auth-form">
                <label className="auth-label" htmlFor="reset-code">
                  Reset code
                </label>
                <input
                  id="reset-code"
                  type="text"
                  className="auth-input"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="Enter 6-digit code"
                  disabled={loading}
                />

                <label className="auth-label" htmlFor="reset-password">
                  New password
                </label>
                <PasswordInput
                  id="reset-password"
                  value={password}
                  onChange={setPassword}
                  placeholder="At least 8 characters"
                  disabled={loading}
                  autoComplete="new-password"
                />

                <label className="auth-label" htmlFor="reset-confirm-password">
                  Confirm password
                </label>
                <PasswordInput
                  id="reset-confirm-password"
                  value={confirmPassword}
                  onChange={setConfirmPassword}
                  placeholder="Repeat new password"
                  disabled={loading}
                  autoComplete="new-password"
                />

                <button type="submit" className="btn btn-solid auth-submit" disabled={loading}>
                  {loading ? 'Resetting password...' : 'Reset password'}
                </button>
                <button
                  type="button"
                  className="btn btn-ghost auth-back"
                  onClick={() => {
                    setStep('request');
                    setMessage('');
                  }}
                  disabled={loading}
                >
                  Back
                </button>
              </form>
            )}

            <p className="auth-footer">
              <Link to="/login" className="auth-link">
                Return to sign in
              </Link>
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}

export default ForgotPassword;
